import { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Storey multiplier: each additional storey adds 20% to base volume
const STOREY_MULTIPLIER = 0.20;

const DCWMPStep3DemolitionPhase = ({ project, onComplete, onBack }) => {
  const [formData, setFormData] = useState({
    has_demolition: false,
    demolition_components: [{ 
      demolition_type: 'commercial_industrial',
      description: '', 
      gfa: '', 
      storeys: 1, 
      construction_era: 'post_1990' 
    }],
    demolition_rate_override: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Rates reduced by 30% (Jan 2025)
  const demolitionTypes = [
    { value: 'detached_dwelling', label: 'Detached Dwelling', rate: 0.77 },
    { value: 'townhouse_lowrise', label: 'Townhouse / Low-rise Residential', rate: 0.77 },
    { value: 'midrise_residential', label: 'Mid-rise Residential', rate: 0.84 },
    { value: 'commercial_industrial', label: 'Commercial / Industrial', rate: 0.91 },
    { value: 'partial_stripout', label: 'Partial Demolition / Internal Strip-out', rate: 0.35 }
  ];

  useEffect(() => {
    if (project?.dcwmp_step3) {
      const step3 = project.dcwmp_step3;
      setFormData({
        has_demolition: step3.has_demolition || false,
        demolition_components: step3.demolition_components?.length > 0 
          ? step3.demolition_components.map(c => ({
              demolition_type: c.demolition_type || step3.demolition_type || 'commercial_industrial',
              description: c.description || '',
              gfa: c.gfa || '',
              storeys: c.storeys || 1,
              construction_era: c.construction_era || 'post_1990'
            }))
          : [{ demolition_type: 'commercial_industrial', description: '', gfa: '', storeys: 1, construction_era: 'post_1990' }],
        demolition_rate_override: step3.demolition_rate_override || ''
      });
    }
  }, [project]);

  const addComponent = () => {
    setFormData(prev => ({
      ...prev,
      demolition_components: [...prev.demolition_components, { 
        demolition_type: 'commercial_industrial',
        description: '', 
        gfa: '', 
        storeys: 1, 
        construction_era: 'post_1990' 
      }]
    }));
  };

  const removeComponent = (index) => {
    if (formData.demolition_components.length > 1) {
      setFormData(prev => ({
        ...prev,
        demolition_components: prev.demolition_components.filter((_, i) => i !== index)
      }));
    }
  };

  const updateComponent = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      demolition_components: prev.demolition_components.map((comp, i) => 
        i === index ? { ...comp, [field]: value } : comp
      )
    }));
  };

  const getTotalGFA = () => {
    return formData.demolition_components.reduce((sum, comp) => {
      return sum + (parseFloat(comp.gfa) || 0);
    }, 0);
  };

  const getRateForComponent = (comp) => {
    const selected = demolitionTypes.find(t => t.value === comp.demolition_type);
    return selected?.rate || 0.91;
  };

  // Calculate storey factor: 1 storey = 1.0, 2 storeys = 1.2, 3 storeys = 1.4, etc.
  const getStoreyFactor = (storeys) => {
    return 1 + (Math.max(parseInt(storeys) || 1, 1) - 1) * STOREY_MULTIPLIER;
  };

  // Calculate waste for a single component (with storey multiplier)
  const getComponentWaste = (comp) => {
    const gfa = parseFloat(comp.gfa) || 0;
    const rate = formData.demolition_rate_override 
      ? parseFloat(formData.demolition_rate_override) 
      : getRateForComponent(comp);
    const storeyFactor = getStoreyFactor(comp.storeys);
    return gfa * rate * storeyFactor;
  };

  const getEstimatedWaste = () => {
    // Calculate waste for each component using its own rate and storey multiplier
    return formData.demolition_components.reduce((sum, comp) => {
      return sum + getComponentWaste(comp);
    }, 0);
  };

  const getAverageRate = () => {
    if (formData.demolition_rate_override) {
      return parseFloat(formData.demolition_rate_override);
    }
    const totalGFA = getTotalGFA();
    if (totalGFA === 0) return 1.2;
    
    // Weighted average of rates
    const weightedSum = formData.demolition_components.reduce((sum, comp) => {
      const gfa = parseFloat(comp.gfa) || 0;
      return sum + (gfa * getRateForComponent(comp));
    }, 0);
    return weightedSum / totalGFA;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const token = localStorage.getItem('opwmp_token');
      
      let payload = {
        has_demolition: formData.has_demolition,
        demolition_components: [],
        demolition_rate_override: formData.demolition_rate_override ? parseFloat(formData.demolition_rate_override) : null
      };

      if (formData.has_demolition) {
        payload.demolition_components = formData.demolition_components
          .filter(c => c.description && c.gfa)
          .map(c => ({
            demolition_type: c.demolition_type,
            description: c.description,
            gfa: parseFloat(c.gfa),
            storeys: parseInt(c.storeys) || 1,
            construction_era: c.construction_era
          }));
      }

      await axios.put(
        `${API}/api/projects/${project.id}/dcwmp/step3`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      onComplete();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white shadow-lg rounded-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-600 to-amber-700 px-8 py-6">
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center px-3 py-1.5 border border-white/30 rounded-md text-sm font-medium text-white bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        </div>
        <h2 className="text-2xl font-bold text-white">Demolition Phase</h2>
        <p className="text-amber-100 mt-1">Define any demolition works for this project</p>
      </div>

      <form onSubmit={handleSubmit} className="px-8 py-6 space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Has Demolition Toggle */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.has_demolition}
              onChange={(e) => setFormData(prev => ({ ...prev, has_demolition: e.target.checked }))}
              className="h-5 w-5 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
            />
            <span className="ml-3 text-sm font-medium text-gray-700">
              This project includes demolition works
            </span>
          </label>
        </div>

        {formData.has_demolition && (
          <>
            {/* Demolition Components - Each with its own type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Structures to be Demolished <span className="text-red-500">*</span>
              </label>
              
              <div className="space-y-4">
                {formData.demolition_components.map((comp, index) => (
                  <div key={index} className="p-4 bg-amber-50 border-2 border-amber-300 rounded-lg shadow-sm">
                    {/* Structure Header with Remove Button */}
                    <div className="flex justify-between items-center mb-3 pb-2 border-b border-amber-200">
                      <span className="text-sm font-semibold text-amber-800">
                        Structure {index + 1}
                      </span>
                      {formData.demolition_components.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeComponent(index)}
                          className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                          title="Remove structure"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>

                    {/* Demolition Type Dropdown */}
                    <div className="mb-3">
                      <label className="block text-xs text-gray-500 mb-1">Demolition Type</label>
                      <select
                        value={comp.demolition_type}
                        onChange={(e) => updateComponent(index, 'demolition_type', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-amber-500 focus:border-amber-500 bg-white"
                      >
                        {demolitionTypes.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.label} (Rate: {type.rate} m³/m²)
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Structure Details Row */}
                    <div className="grid grid-cols-12 gap-3">
                      <div className="col-span-5">
                        <label className="block text-xs text-gray-500 mb-1">Structure Description</label>
                        <input
                          type="text"
                          placeholder="e.g., Existing warehouse"
                          value={comp.description}
                          onChange={(e) => updateComponent(index, 'description', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-amber-500 focus:border-amber-500"
                        />
                      </div>
                      <div className="col-span-3">
                        <label className="block text-xs text-gray-500 mb-1">GFA (m²)</label>
                        <input
                          type="number"
                          placeholder="m²"
                          value={comp.gfa}
                          onChange={(e) => updateComponent(index, 'gfa', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-amber-500 focus:border-amber-500"
                          min="0"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs text-gray-500 mb-1">Storeys</label>
                        <input
                          type="number"
                          value={comp.storeys}
                          onChange={(e) => updateComponent(index, 'storeys', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-amber-500 focus:border-amber-500"
                          min="1"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs text-gray-500 mb-1">Era</label>
                        <select
                          value={comp.construction_era}
                          onChange={(e) => updateComponent(index, 'construction_era', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-amber-500 focus:border-amber-500"
                        >
                          <option value="pre_1990">Pre-1990</option>
                          <option value="post_1990">Post-1990</option>
                        </select>
                      </div>
                    </div>

                    {/* Per-structure estimate */}
                    {comp.gfa && (
                      <div className="mt-2 text-xs text-amber-700 text-right">
                        Est. waste: {getComponentWaste(comp).toLocaleString(undefined, {maximumFractionDigits: 0})} m³
                        {parseInt(comp.storeys) > 1 && (
                          <span className="text-gray-500 ml-1">
                            (incl. +{((parseInt(comp.storeys) - 1) * 20)}% for {comp.storeys} storeys)
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={addComponent}
                className="mt-4 inline-flex items-center px-4 py-2 border border-amber-300 text-sm font-medium rounded-md text-amber-700 bg-amber-50 hover:bg-amber-100"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Another Structure
              </button>
            </div>

            {/* Rate Override (Advanced) */}
            <div className="border-t pt-4">
              <details className="text-sm">
                <summary className="cursor-pointer text-gray-600 hover:text-gray-800 font-medium">
                  Advanced: Override waste generation rate for all structures
                </summary>
                <div className="mt-3 pl-4">
                  <label className="block text-xs text-gray-500 mb-1">
                    Custom rate (m³ per m² GFA) - Leave blank to use individual rates
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="Leave blank for individual rates"
                    value={formData.demolition_rate_override}
                    onChange={(e) => setFormData(prev => ({ ...prev, demolition_rate_override: e.target.value }))}
                    className="w-40 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-amber-500 focus:border-amber-500"
                    min="0.1"
                    max="3"
                  />
                </div>
              </details>
            </div>

            {/* Estimated Waste Summary */}
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-sm text-gray-600">Total GFA</p>
                  <p className="text-xl font-bold text-amber-700">{getTotalGFA().toLocaleString()} m²</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Avg. Rate</p>
                  <p className="text-xl font-bold text-amber-700">{getAverageRate().toFixed(2)} m³/m²</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Est. Waste</p>
                  <p className="text-xl font-bold text-amber-700">{getEstimatedWaste().toLocaleString()} m³</p>
                </div>
              </div>
            </div>
          </>
        )}

        {!formData.has_demolition && (
          <div className="bg-gray-50 rounded-lg p-6 text-center">
            <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            <p className="text-gray-600">No demolition works for this project</p>
            <p className="text-sm text-gray-500 mt-1">Click the checkbox above if demolition is required</p>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between pt-6 border-t">
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
          >
            Back
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save & Continue'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DCWMPStep3DemolitionPhase;
